import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Box,
  useTheme,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  ButtonGroup,
} from "@mui/material";
import { Delete, ArrowBack, ArrowForward, Add } from "@mui/icons-material";
import { PageContainer, Breadcrumb, PatientModal } from "../common";
import Loading from "../common/Loading";
import { client } from "src/common/axiosClient";
import moment from "moment";

export default function PatientList() {
  const theme = useTheme();
  const borderColor = theme.palette.divider;

  const [isLoading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [totalPage, setTotalPage] = useState<number>(1);

  // 모달 상태 관리
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [patientData, setPatientData] = useState({
    name: "",
    birthDate: moment().subtract(20, "years").format("YYYY-MM-DD"),
    gender: "",
    mobile: "",
  });

  // 휴대폰 번호 정규식 유효성 검사 함수
  const isValidMobile = (mobile: string) => {
    const mobileRegex = /^[0-9]{3}-[0-9]{4}-[0-9]{4}$/;
    return mobileRegex.test(mobile);
  };

  useEffect(() => {
    getPatientList();
  }, [currentPage, perPage]);

  const getPatientList = async () => {
    setLoading(true);
    const response = await client.get(
      `admin/api/v1/patient/all?page=${currentPage}&perPage=${perPage}`
    );
    if (response.status === 200) {
      setData(response.data.content);
      setTotalPage(response.data.paging.totalPage);
    } else {
      console.error(response);
    }
    setLoading(false);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleDelete = async (id: number) => {
    const confirmDelete = window.confirm("정말로 이 환자를 삭제하시겠습니까?");
    if (!confirmDelete) {
      return;
    }
    try {
      const response = await client.delete(`admin/api/v1/patient/${id}`);
      if (response.status === 200) {
        alert("환자가 성공적으로 삭제되었습니다.");
        getPatientList(); // Refresh the patient list
      }
    } catch (error) {
      console.error("환자 삭제 중 오류가 발생했습니다: ", error);
    }
  };

  // 모달 핸들러
  const handleOpenModal = () => {
    setOpenModal(true);
    // 모달 열 때 생년월일 필드를 20년 전 날짜로 초기화
    setPatientData({
      ...patientData,
      birthDate: moment().subtract(20, "years").format("YYYY-MM-DD"),
    });
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    // 모달 닫을 때 입력 필드 초기화
    setPatientData({
      name: "",
      birthDate: moment().subtract(20, "years").format("YYYY-MM-DD"),
      gender: "",
      mobile: "",
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // 휴대폰 번호 자동 하이픈 삽입
    if (name === "mobile") {
      // 숫자만 추출
      const numbersOnly = value.replace(/[^0-9]/g, "");

      // 적절한 위치에 하이픈 삽입
      let formattedNumber = "";
      if (numbersOnly.length <= 3) {
        formattedNumber = numbersOnly;
      } else if (numbersOnly.length <= 7) {
        formattedNumber = `${numbersOnly.slice(0, 3)}-${numbersOnly.slice(3)}`;
      } else {
        formattedNumber = `${numbersOnly.slice(0, 3)}-${numbersOnly.slice(
          3,
          7
        )}-${numbersOnly.slice(7, 11)}`;
      }

      setPatientData({
        ...patientData,
        [name]: formattedNumber,
      });
    } else {
      setPatientData({
        ...patientData,
        [name]: value,
      });
    }
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    setPatientData({
      ...patientData,
      [name]: value,
    });
  };

  const handleSubmit = async () => {
    if (
      !patientData.name ||
      !patientData.birthDate ||
      !patientData.gender ||
      !patientData.mobile
    ) {
      alert("모든 필드를 입력해주세요.");
      return;
    }

    // 휴대폰 번호 형식 검증
    if (!isValidMobile(patientData.mobile)) {
      alert("휴대폰 번호는 000-0000-0000 형식으로 입력해주세요.");
      return;
    }

    try {
      setLoading(true);
      const response = await client.post("admin/api/v1/patient", {
        name: patientData.name,
        birthDate: patientData.birthDate,
        gender: patientData.gender,
        mobile: patientData.mobile,
      });
      if (response.status === 201) {
        alert("환자 등록이 완료되었습니다.");
        getPatientList(); // 환자 목록 새로고침
        handleCloseModal(); // 모달 닫기
      }
    } catch (error) {
      console.error("환자 등록 중 오류가 발생했습니다: ", error);
      alert("환자 등록에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 빠른 생년월일 선택
  const handleQuickBirthDate = (years: number) => {
    setPatientData({
      ...patientData,
      birthDate: moment().subtract(years, "years").format("YYYY-MM-DD"),
    });
  };

  return (
    <PageContainer>
      <Loading isLoading={isLoading} />
      <Breadcrumb
        action={
          <div className="space-x-5">
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={handleOpenModal}
            >
              환자 등록
            </Button>
          </div>
        }
      >
        <div className="flex items-center">
          <Typography variant="h6">환자 관리</Typography>
        </div>
      </Breadcrumb>

      <Card sx={{ padding: "10px", border: `1px solid ${borderColor}` }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>No.</TableCell>
                <TableCell>이름</TableCell>
                <TableCell>생년월일</TableCell>
                <TableCell>성별</TableCell>
                <TableCell>전화번호</TableCell>
                <TableCell>최근 방문일</TableCell>
                <TableCell>등록일</TableCell>
                <TableCell>삭제</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((patient, index) => (
                <TableRow key={patient.id}>
                  <TableCell>{patient.id}</TableCell>
                  <TableCell>{patient.name}</TableCell>
                  <TableCell>
                    {moment(patient.birthDate).format("YYYY-MM-DD")}
                  </TableCell>
                  <TableCell>
                    {patient.gender === "MALE" ? "남자" : "여자"}
                  </TableCell>
                  <TableCell>{patient.mobile}</TableCell>
                  <TableCell>
                    {patient.lastVisitAt
                      ? moment(patient.lastVisitAt).format("YYYY-MM-DD")
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {moment(patient.createdAt).format("YYYY-MM-DD")}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(patient.id)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="center" mt={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            이전
          </Button>
          <Typography variant="body2" mx={2}>
            {currentPage} / {totalPage}
          </Typography>
          <Button
            variant="outlined"
            endIcon={<ArrowForward />}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPage}
          >
            다음
          </Button>
        </Box>
      </Card>

      {/* 환자 등록 모달 */}
      <PatientModal
        open={openModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        patientData={patientData}
        handleInputChange={handleInputChange}
        handleSelectChange={handleSelectChange}
        handleQuickBirthDate={handleQuickBirthDate}
      />
    </PageContainer>
  );
}
